/* Help content lives in the repo as plain strings so it versions with the code
   and needs no CMS. Adding an app = adding an entry here plus a launcher card.

   Written for the reader, not the system: each answer says what to DO, and the
   questions are phrased the way a parent would actually ask them. */

export const HELP_SECTIONS = [
  {
    id: "availability",
    title: "Availability — for parents",
    intro:
      "Tell your coach which days your player can't make it. There's no account and no password.",
    items: [
      {
        q: "How do I get in?",
        a: [
          "Use the link your coach or the club emailed you, or scan the QR code they shared. That link is all you need — bookmark it and come back any time.",
          "If you were given a team passcode instead, open the app, pick your team, and enter it.",
        ],
      },
      {
        q: "How do I mark days?",
        a: [
          "Tap any day your player CAN'T make it. Tap it again to mark only part of that day — mornings but not evenings, say.",
          "Then hit Save. Your coach sees it straight away, and you can come back and change it whenever plans change.",
        ],
      },
      {
        q: "What if we're available every day?",
        a: [
          "Open it and hit Save without marking anything. That still counts as an answer, and it tells your coach they can count on you — which is different from not having replied.",
        ],
      },
      {
        q: "I have more than one player",
        a: [
          "If the club has your email on file, your link covers all of your players at once — even if they're on different teams. Pick one, set their dates, then come back and pick the other.",
          "If you only have a team passcode, you'll need each team's passcode separately. Ask the club to add your email to the roster and they can send you a single link instead.",
        ],
      },
      {
        q: "Can I change my dates later?",
        a: ["Yes, any time. Open your link, change what you need, and Save again."],
      },
    ],
  },
  {
    id: "coaches",
    title: "Availability — for coaches",
    intro: "See your whole team's availability at a glance.",
    items: [
      {
        q: "Getting in",
        a: [
          "Use your COACH passcode, not the parent one. Same team, different code — the coach code is what unlocks the team-wide view.",
        ],
      },
      {
        q: "Reading the calendar",
        a: [
          "Each day shows how many players are unavailable. Darker means more players out.",
          "Tap a day to see exactly who, and whether they're out all day or only part of it.",
        ],
      },
      {
        q: "Nobody has filled anything in",
        a: [
          "Tap Invite team. You'll find your team's parent link to paste into a team chat, a QR code to print or show at practice, and — for any family with an email on the roster — a button to email each of them a personal link.",
          "Nudge families who haven't replied chases only the stragglers. It skips anyone who already answered, including families who are available every day.",
          "Once some families have answered, the same 'Remind families who haven't replied' button appears right on your dashboard, above the players list.",
          "You don't need the club admin for any of this. Everything you send is limited to your own team — and a team-wide nudge is capped at once per day, so it's safe to leave for whoever checks the app first.",
        ],
      },
      {
        q: "Adding a player, or a fellow coach",
        a: [
          "Tap Edit roster. Add players one per line, and put an email on a family with ✉ so they can be sent a link.",
          "Add your assistant coaches and team manager with Add as Coach/Staff. They mark their own dates the same way families do, and they're kept out of the player percentages — a coach being away doesn't make a date look unplayable.",
          "Removing someone is the club admin's job. It deletes their availability along with them and can't be undone.",
        ],
      },
    ],
  },
  {
    id: "gear",
    title: "Gear Exchange",
    intro: "Buy and sell used soccer gear with other SSA families.",
    items: [
      {
        q: "How does it work?",
        a: [
          "Members list gear they've outgrown. You message the seller through the app, and the two of you arrange the handover.",
          "THERE ARE NO PAYMENTS IN THE APP. Nothing is charged, held, or refunded here — you sort out money between yourselves.",
        ],
      },
      {
        q: "Staying safe",
        a: [
          "Meet somewhere public. The fields before or after a session work well and everyone's already there.",
          "Take a look at the item before you hand over money. Cash is simplest for a small swap.",
          "Something wrong with a listing? Open it and use Report this listing — it goes straight to the SSA admins and the seller isn't told who reported it.",
        ],
      },
      {
        q: "Creating an account",
        a: [
          "You need the SSA member code — ask a club admin if you don't have one. It keeps the marketplace to Sartell families.",
          "An admin can also invite you directly by email, in which case you skip the code entirely.",
          "Forgot your password? Use the 'Forgot your password?' link on the sign-in page.",
        ],
      },
      {
        q: "Listing something",
        a: [
          "Add a few photos, a title, a category and a price. Enter 0 if you're giving it away.",
          "The first photo is the cover — the one people see when browsing. Drag or re-add photos to change which comes first.",
          "Mark it Sold when it's gone, or delete it. You can edit anything about a listing after posting.",
        ],
      },
      {
        q: "Getting told when something appears",
        a: [
          "Use Alerts. Save what you're after — cleats in 3Y, say — and you'll get an email when someone lists a match.",
          "Every alert email has an unsubscribe link.",
        ],
      },
    ],
  },
  {
    id: "admins",
    title: "For SSA admins",
    intro: "Running the apps for the club.",
    items: [
      {
        q: "Setting up teams and rosters",
        a: [
          "Upload a CSV from the admin screen — one row per player. There are templates on that screen showing the format.",
          "Add a Parent Email column and you can email each family a personal link instead of handing out shared passcodes. Put the same email on every one of a family's players, including players on different teams, and they get one link covering all of them.",
          "You don't need the emails up front. Open a team's Roster and click ✉ on any player to add, change or remove one at any point in the season — green means it's on file. Re-uploading a CSV without an email column won't wipe the ones you've added.",
          "Typing players in by hand works too: one per line, as 'Name, Jersey, Parent Email' — the jersey and email are both optional.",
        ],
      },
      {
        q: "Getting parents started",
        a: [
          "Admin → 'Email parents their link' shows you exactly who would be emailed before anything sends. Send to everyone, or one family at a time.",
          "Families with no email on the roster keep using the team passcode — nothing breaks for them.",
          "The email itself carries the club logo, a color key for what red/amber/green mean on the calendar, and step-by-step instructions for signing in with the team passcode — including that team's parent passcode — so a family who loses the link can still get in without asking you.",
          "The Families send never contains a coach passcode. The Coaches & staff send does: it also explains that the team has two passcodes and how to open the team view with the coach one, so a new coach can get going without emailing you first. That message only goes to people you've marked as Coaches & staff on a roster.",
        ],
      },
      {
        q: "Chasing people who haven't replied",
        a: [
          "Admin → 'Nudge families who haven't replied'. It counts only genuinely-missing responses; a family who saved 'available every day' is treated as having answered and won't be nagged.",
          "It also lists players with no email on file, since those can't be reminded and you'll want to chase them another way.",
        ],
      },
      {
        q: "Season rollover",
        a: [
          "Create the new teams and upload the new rosters, then re-send the family links.",
          "To make last season's links stop working, change the FAMILY_LINK_SALT app setting. Every previously emailed link stops working at once, which is usually what you want at rollover.",
        ],
      },
      {
        q: "Gear Exchange moderation",
        a: [
          "Reported listings appear at the top of the Gear admin screen. Dismiss a report, mark it handled, or open the listing and Remove it.",
          "You can also deactivate a member (reversible — hides their listings, keeps their messages) or delete them outright (permanent, removes everything they posted).",
        ],
      },
    ],
  },
];
